import { formatMoney } from '@theme/money-formatting';
import { calculateSetDeal } from './frest-set-deal.js';

// Sets on the same page share a single cart mutation lane.
let activeSet = null;
let reviewRequired = false;
const connectedSets = new Set();
const refreshSets = () => { for (const set of connectedSets) set.refreshControls(); };
// Returning from the cart can restore the successful mutation lock from BFCache.
// Refresh rendered availability and controls without ever repeating the POST.
window.addEventListener('pageshow', event => {
    if (event.persisted && (reviewRequired || activeSet !== null)) window.location.reload();
});

// Add original variants together; Shopify calculates the cart's actual discounts.
class FrestArtSet extends HTMLElement {
    #lifecycle;
    #items;
    #products;
    #cards;
    #loadingSlots = new Set();
    #form;
    #button;
    #status;
    #busy = false;
    #blocked = false;
    #postAttempted = false;
    #deal;

    connectedCallback() {
        this.#lifecycle?.abort();
        this.#lifecycle = new AbortController();
        this.#form = this.querySelector('[data-art-set-form]');
        this.#button = this.querySelector('[data-add-art-set]');
        this.#status = this.querySelector('[data-art-set-status]');
        this.#cards = Array.from(this.querySelectorAll('[data-art-set-artwork]'));
        connectedSets.add(this);
        try {
            if (!this.#form || !this.#button || !this.#status) throw new TypeError('Invalid art set');
            const productPayload = this.querySelector('[data-art-set-products]');
            if (productPayload) {
                const renderedProducts = JSON.parse(productPayload.textContent);
                if (!Array.isArray(renderedProducts)) throw new TypeError('Invalid products');
                // A retained builder can reconnect with runtime choices absent from its
                // initial [] payload. Preserve only the records still selected in its DOM.
                const selectedIds = new Set(this.#cards.map(card => Number(card.querySelector('[data-art-set-product]')?.value)).filter(Boolean));
                const retainedProducts = this.hasAttribute('data-art-set-builder') && Array.isArray(this.#products)
                    ? this.#products.filter(product => selectedIds.has(product.productId)) : [];
                if (new Set(renderedProducts.map(product => product.productId)).size !== renderedProducts.length)
                    throw new TypeError('Duplicate products');
                const productsById = new Map(retainedProducts.map(product => [product.productId, product]));
                for (const product of renderedProducts) productsById.set(product.productId, product);
                this.#products = [...productsById.values()];
                this.#initializeProducts();
            } else {
                // Existing merchant sections remain compatible during a staged update.
                const items = JSON.parse(this.querySelector('[data-art-set-items]')?.textContent ?? 'null');
                if (!Array.isArray(items) || items.length !== 4
                    || items.some(item => !Number.isSafeInteger(item.id) || item.id <= 0 || item.quantity !== 1)
                    || new Set(items.map(item => item.id)).size !== 4) throw new TypeError('Invalid art set');
                this.#items = items.map(({ id }) => ({ id, quantity: 1 }));
            }
        } catch {
            this.#blocked = true;
        }
        if (this.#status) {
            this.#status.setAttribute('role', 'status');
            this.#status.setAttribute('aria-live', 'polite');
        }
        this.#setBusy(false);
        this.#form?.addEventListener('submit', this.#submit, { signal: this.#lifecycle.signal });
        this.addEventListener('change', this.#changeChoice, { signal: this.#lifecycle.signal });
        if (this.#blocked) this.#showReview();
    }

    disconnectedCallback() {
        // A disconnected component cannot safely repeat an in-flight mutation.
        if (this.#postAttempted) {
            this.#blocked = true;
            reviewRequired = true;
        }
        connectedSets.delete(this);
        if (activeSet === this) activeSet = null;
        refreshSets();
        this.#lifecycle?.abort();
    }

    #validateProduct(product) {
        if (!Number.isSafeInteger(product?.productId) || product.productId <= 0
            || typeof product.url !== 'string' || !Array.isArray(product.variants) || !product.variants.length)
            throw new TypeError('Invalid product');
        const url = new URL(product.url, window.location.origin);
        if (url.origin !== window.location.origin) throw new TypeError('Invalid product route');
        for (const variant of product.variants) {
            if (!Number.isSafeInteger(variant.id) || variant.id <= 0
                || !Number.isSafeInteger(variant.price) || variant.price < 0
                || typeof variant.title !== 'string' || !variant.title.trim()
                || typeof variant.priceText !== 'string') throw new TypeError('Invalid variant');
        }
        if (new Set(product.variants.map(variant => variant.title)).size !== product.variants.length)
            throw new TypeError('Ambiguous variants');
        if (this.hasAttribute('data-art-set-builder')
            && (typeof product.title !== 'string' || !product.title.trim()
                || typeof product.imageUrl !== 'string' || !product.imageUrl.trim()))
            throw new TypeError('Missing artwork display');
    }

    #initializeProducts() {
        const builder = this.hasAttribute('data-art-set-builder');
        if (!Array.isArray(this.#products)
            || (!builder && this.#products.length !== 4) || this.#cards.length !== 4
            || new Set(this.#products.map(product => product.productId)).size !== this.#products.length)
            throw new TypeError('Invalid products');
        for (const product of this.#products) this.#validateProduct(product);
        for (let index = 0; index < 4; index++) this.#hydrateCard(index);
        this.#applyChoices();
    }

    #productForCard(index) {
        const choice = this.#cards[index].querySelector('[data-art-set-product]');
        return choice ? this.#products.find(product => product.productId === Number(choice.value)) : this.#products[index];
    }

    #hydrateCard(index) {
        const card = this.#cards[index];
        const product = this.#productForCard(index);
        const select = card.querySelector('[data-art-set-size]');
        if (!select) throw new TypeError('Missing size selection');
        const empty = !product;
        if (empty && (!this.hasAttribute('data-art-set-builder')
            || card.querySelector('[data-art-set-product]')?.value !== '')) throw new TypeError('Invalid artwork selection');
        card.dataset.empty = String(empty);
        card.dataset.selected = String(!empty);
        const emptyDisplay = card.querySelector('[data-art-set-empty]');
        const filledDisplay = card.querySelector('[data-art-set-filled]');
        if (emptyDisplay) emptyDisplay.hidden = !empty;
        if (filledDisplay) filledDisplay.hidden = empty;
        if (empty) {
            select.replaceChildren();
            const image = card.querySelector('[data-art-set-image]');
            if (image) { image.removeAttribute('src'); image.removeAttribute('srcset'); image.alt = ''; image.hidden = true; }
            const title = card.querySelector('[data-art-set-title]');
            if (title) title.textContent = '';
            const price = card.querySelector('[data-art-set-price]');
            if (price) price.textContent = '';
            card.querySelectorAll('[data-art-set-variant-link]').forEach(link => link.removeAttribute('href'));
            const control = card.querySelector('[data-art-set-size-control]');
            if (control) control.hidden = true;
            return;
        }
        const previousSize = select.value;
        select.replaceChildren(...product.variants.map(variant => {
            const option = document.createElement('option');
            option.value = variant.title;
            option.textContent = variant.title;
            return option;
        }));
        const preferred = this.dataset.defaultSize || '20x25 cm / 8x10 in';
        select.value = product.variants.some(variant => variant.title === previousSize) ? previousSize
            : product.variants.some(variant => variant.title === preferred) ? preferred : product.variants[0].title;
        const image = card.querySelector('[data-art-set-image]');
        if (image && product.imageUrl) {
            image.hidden = false;
            image.src = product.imageUrl;
            image.removeAttribute('srcset');
            image.alt = product.imageAlt || product.title;
        }
        const title = card.querySelector('[data-art-set-title]');
        if (title && product.title) title.textContent = product.title;
        const control = card.querySelector('[data-art-set-size-control]');
        if (control) control.hidden = false;
    }

    #changeChoice = event => {
        if (this.#busy || this.#blocked || activeSet || reviewRequired) return;
        const target = event.target;
        if (!(target instanceof HTMLSelectElement)
            || !target.matches('[data-art-set-size], [data-art-set-product]')) return;
        const index = this.#cards.findIndex(card => card.contains(target));
        if (index < 0 || this.#loadingSlots.has(index)) return;
        try {
            if (target.matches('[data-art-set-product]')) this.#hydrateCard(index);
            this.#applyChoices();
        } catch {
            this.#blocked = true;
            this.#showReview();
        }
        this.refreshControls();
    };

    #applyChoices() {
        const products = this.#cards.map((card, index) => this.#productForCard(index));
        const variants = products.map((product, index) => product?.variants.find(variant =>
            variant.title === this.#cards[index].querySelector('[data-art-set-size]')?.value));
        if (variants.some((variant, index) => products[index] && !variant)
            || (!this.hasAttribute('data-art-set-builder') && variants.some(variant => !variant)))
            throw new TypeError('Invalid size');
        let total = 0;
        variants.forEach((variant, index) => {
            if (!variant) return;
            total += variant.price;
            const url = new URL(products[index].url, window.location.origin);
            url.searchParams.set('variant', String(variant.id));
            this.#cards[index].querySelectorAll('[data-art-set-variant-link]').forEach(link => { link.href = url.href; });
            const price = this.#cards[index].querySelector('[data-art-set-price]');
            if (price) price.textContent = variant.priceText;
        });
        const totalNode = this.querySelector('[data-art-set-total]');
        if (!totalNode || !Number.isSafeInteger(total) || !this.dataset.moneyFormat || !this.dataset.currency)
            throw new TypeError('Invalid price');
        const plainMoney = document.createElement('template');
        plainMoney.innerHTML = formatMoney(total, this.dataset.moneyFormat, this.dataset.currency);
        totalNode.textContent = plainMoney.content.textContent;
        const selected = products.filter(Boolean);
        const chosenVariants = variants.filter(Boolean);
        const distinct = new Set(selected.map(product => product.productId)).size === selected.length
            && new Set(chosenVariants.map(variant => variant.id)).size === chosenVariants.length;
        const complete = selected.length === 4 && distinct;
        this.#items = complete ? variants.map(variant => ({ id: variant.id, quantity: 1 })) : undefined;
        this.#status.textContent = distinct ? '' : this.#message('duplicateMessage', 'Choose four different artworks for your set.');
        const count = this.querySelector('[data-art-set-count]');
        if (count) count.textContent = `${selected.length}/4`;
        const totalRow = this.querySelector('[data-build-total-row]');
        if (totalRow) totalRow.hidden = selected.length === 0;
        this.#deal = calculateSetDeal(variants.flatMap((variant, index) => variant
            ? [{ price: variant.price, rewardEligible: products[index].rewardEligible }] : []));
        if (!distinct) this.#deal.available = false;
        this.#renderDeal();
        this.dispatchEvent(new CustomEvent('frest:art-set-selection', {
            bubbles: true, detail: { count: selected.length, total, complete, productIds: products.map(product => product?.productId ?? null) },
        }));
    }

    #renderDeal() {
        if (this.dataset.dealEnabled !== 'true') return;
        const deal = this.#deal;
        const unavailable = !deal || this.#blocked || this.#busy || reviewRequired
            || activeSet !== null || this.#loadingSlots.size > 0;
        const visible = !unavailable && deal.complete && deal.available;
        for (const selector of ['price-row', 'savings-row', 'extra-row']) {
            const row = this.querySelector(`[data-set-deal-${selector}]`);
            if (row) row.hidden = !visible;
        }
        const bogoRow = this.querySelector('[data-set-deal-bogo-row]');
        if (bogoRow) bogoRow.hidden = !visible || !deal.bogoSavings;
        const badge = this.querySelector('[data-set-deal-badge]');
        if (badge) badge.textContent = visible
            ? this.#message('dealReadyMessage', 'Extra 10% unlocked')
            : this.#message('dealBadgeMessage', '4+ · Extra 10% off');
        const progress = this.querySelector('[data-set-deal-progress]');
        if (progress) {
            progress.hidden = visible;
            progress.textContent = unavailable || (deal.complete && !deal.available)
                ? this.#message('dealUnavailableMessage', 'Final offers confirmed in cart')
                : this.#message('dealProgressMessage', 'Choose __COUNT__ more to unlock extra 10%').replace('__COUNT__', String(4 - deal.count));
        }
        if (!visible) return;
        for (const [key, value] of [['price', deal.total], ['savings', deal.savings], ['bogo', deal.bogoSavings], ['extra', deal.extraSavings]]) {
            const node = this.querySelector(`[data-set-deal-${key}]`);
            if (!node) continue;
            const money = document.createElement('template');
            money.innerHTML = formatMoney(value, this.dataset.moneyFormat, this.dataset.currency);
            node.textContent = money.content.textContent;
        }
    }

    /** Read a selected catalogue record for contextual artwork discovery. */
    getRegisteredArtwork(productId) {
        return this.#products?.find(product => product.productId === Number(productId));
    }

    /** Register an eligible, validated physical artwork returned by the picker. */
    registerArtwork(product, slotIndex) {
        if (!this.hasAttribute('data-art-set-builder') || !Number.isInteger(slotIndex) || slotIndex < 0 || slotIndex > 3
            || this.#busy || this.#blocked || activeSet || reviewRequired) throw new TypeError('Picker unavailable');
        this.#validateProduct(product);
        const existing = this.#products.findIndex(item => item.productId === product.productId);
        if (existing >= 0) this.#products[existing] = product;
        else this.#products.push(product);
        for (const card of this.#cards) {
            const select = card.querySelector('[data-art-set-product]');
            if (!select) throw new TypeError('Missing product selector');
            if (!Array.from(select.options).some(option => Number(option.value) === product.productId)) {
                const option = document.createElement('option');
                option.value = String(product.productId);
                option.textContent = product.title;
                select.append(option);
            }
        }
        this.#cards[slotIndex].querySelector('[data-art-set-product]').value = String(product.productId);
        // An updated record can also be present in another slot; refresh each affected card.
        for (let index = 0; index < 4; index++) {
            if (this.#productForCard(index)?.productId === product.productId) this.#hydrateCard(index);
        }
        this.#applyChoices();
        this.refreshControls();
    }

    /** Empty a builder slot without discarding other selected artworks or sizes. */
    clearArtwork(slotIndex) {
        if (!this.hasAttribute('data-art-set-builder') || !Number.isInteger(slotIndex) || slotIndex < 0 || slotIndex > 3
            || this.#busy || this.#blocked || activeSet || reviewRequired || this.#loadingSlots.has(slotIndex))
            throw new TypeError('Picker unavailable');
        const select = this.#cards[slotIndex].querySelector('[data-art-set-product]');
        if (!select) throw new TypeError('Missing product selector');
        if (!Array.from(select.options).some(option => option.value === '')) {
            const option = document.createElement('option');
            option.value = '';
            select.prepend(option);
        }
        select.value = '';
        this.#hydrateCard(slotIndex);
        this.#applyChoices();
        this.refreshControls();
    }

    setSlotLoading(slotIndex, busy) {
        if (!Number.isInteger(slotIndex) || slotIndex < 0 || slotIndex > 3) throw new TypeError('Invalid artwork slot');
        if (busy) this.#loadingSlots.add(slotIndex);
        else this.#loadingSlots.delete(slotIndex);
        this.#cards[slotIndex]?.setAttribute('aria-busy', String(Boolean(busy)));
        this.refreshControls();
    }

    refreshControls() {
        const locked = this.#busy || this.#blocked || reviewRequired || activeSet !== null;
        if (this.#button) this.#button.disabled = locked || !this.#items || this.#loadingSlots.size > 0;
        this.#cards?.forEach((card, index) => {
            card.querySelectorAll('[data-art-set-size], [data-art-set-product], [data-art-set-open], [data-art-set-clear]').forEach(control => {
                control.disabled = locked || this.#loadingSlots.has(index)
                    || (control.matches('[data-art-set-size]') && card.dataset.empty === 'true');
            });
        });
        this.#renderDeal();
    }

    #url(path) {
        const root = new URL(window.Shopify?.routes?.root ?? '/', window.location.origin);
        if (root.origin !== window.location.origin) throw new TypeError('Invalid storefront route');
        if (!root.pathname.endsWith('/')) root.pathname += '/';
        return new URL(path, root).href;
    }

    async #request(path, options, lifecycle) {
        const controller = new AbortController();
        const cancel = () => controller.abort();
        lifecycle.addEventListener('abort', cancel, { once: true });
        const timeout = window.setTimeout(cancel, 15000);
        try {
            if (lifecycle.aborted) controller.abort();
            const response = await fetch(this.#url(path), {
                credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal,
                headers: { Accept: 'application/json', ...options?.headers },
            });
            if (!response.ok) throw new Error('Cart request failed');
            return await response.json();
        } finally {
            window.clearTimeout(timeout);
            lifecycle.removeEventListener('abort', cancel);
        }
    }

    #quantities(cart) {
        if (!Array.isArray(cart?.items)) throw new TypeError('Invalid cart');
        const quantities = new Map();
        for (const item of cart.items) {
            const id = item.variant_id ?? item.id;
            if (!Number.isSafeInteger(id) || !Number.isSafeInteger(item.quantity) || item.quantity < 0)
                throw new TypeError('Invalid cart item');
            const quantity = (quantities.get(id) ?? 0) + item.quantity;
            if (!Number.isSafeInteger(quantity)) throw new TypeError('Invalid cart quantity');
            quantities.set(id, quantity);
        }
        return quantities;
    }

    #setBusy(busy) {
        this.#busy = busy;
        this.refreshControls();
        this.#form?.setAttribute('aria-busy', String(busy));
    }

    #message(key, fallback) {
        return this.dataset[key] || fallback;
    }

    #showReview() {
        if (!this.#status) return;
        this.#status.textContent = this.#message('uncertainMessage',
            'We could not confirm all four artworks were added. Review your cart before adding more.');
        const link = document.createElement('a');
        link.href = this.#url('cart');
        link.textContent = this.#message('reviewMessage', 'Review your cart');
        this.#status.append(document.createTextNode(' '), link);
    }

    #cartDestination() {
        const cart = new URL(this.#url('cart'));
        const code = this.dataset.promotionCode?.trim();
        if (!code || code.length > 255) return cart.href;
        // Shopify's native shareable-code route preserves the existing cart.
        // The redirect retains the customer's storefront locale.
        const destination = new URL(`/discount/${encodeURIComponent(code)}`, window.location.origin);
        destination.searchParams.set('redirect', cart.pathname + cart.search);
        return destination.href;
    }

    #addItems() {
        if (this.dataset.trackSet !== 'true') return this.#items.map(item => ({ ...item }));
        const setId = window.crypto.randomUUID();
        return this.#items.map((item, index) => ({
            ...item,
            properties: {
                _frest_set_id: setId,
                _frest_set_slot: String(index + 1),
                _frest_set_size: this.#cards[index].querySelector('[data-art-set-size]').value,
            },
        }));
    }

    #goToCart() {
        this.#blocked = true;
        reviewRequired = true;
        refreshSets();
        this.#status.textContent = this.#message('successMessage', 'Artworks added. Opening your cart…');
        window.location.assign(this.#cartDestination());
    }

    #submit = async event => {
        event.preventDefault();
        if (this.#busy || this.#blocked || !this.#items || this.#loadingSlots.size > 0 || activeSet || reviewRequired) return;
        activeSet = this;
        refreshSets();
        const lifecycle = this.#lifecycle.signal;
        this.#setBusy(true);
        this.#status.textContent = this.#message('loadingMessage', 'Adding four artworks…');
        let baseline;
        try {
            baseline = this.#quantities(await this.#request('cart.js', undefined, lifecycle));
            if (lifecycle.aborted) return;
            this.#postAttempted = true;
            const result = await this.#request('cart/add.js', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items: this.#addItems() }),
            }, lifecycle);
            // Existing cart lines report their new total quantity, not the added quantity.
            const added = this.#quantities(result);
            if (added.size !== 4 || this.#items.some(item => (added.get(item.id) ?? 0) < 1))
                throw new TypeError('Incomplete add response');
            if (!lifecycle.aborted) this.#goToCart();
        } catch {
            if (lifecycle.aborted) return;
            if (!this.#postAttempted) {
                this.#status.textContent = this.#message('errorMessage',
                    'We could not check your cart. Please try again.');
                return;
            }
            // An add can have a partial or uncertain result. Reconcile once, never repeat it automatically.
            try {
                const current = this.#quantities(await this.#request('cart.js', undefined, lifecycle));
                if (lifecycle.aborted) return;
                const deltas = this.#items.map(item => (current.get(item.id) ?? 0) - (baseline.get(item.id) ?? 0));
                if (deltas.every(delta => delta === 1)) {
                    this.#goToCart();
                } else {
                    // Even zero delta can precede a late server commit after a network timeout.
                    this.#blocked = true;
                    reviewRequired = true;
                    this.#showReview();
                }
            } catch {
                this.#blocked = true;
                reviewRequired = true;
                if (!lifecycle.aborted) this.#showReview();
            }
        } finally {
            this.#postAttempted = false;
            if (activeSet === this) activeSet = null;
            refreshSets();
            // Do not let an old disconnected request unlock a newly connected component.
            if (!lifecycle.aborted) this.#setBusy(false);
        }
    };
}

if (!customElements.get('frest-art-set')) customElements.define('frest-art-set', FrestArtSet);
